import "./TagifyJobs.css";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useHub, useJobs } from "../../core/Global";
import { API_ROUTES } from "../../core/Constants";
import Icon from "../base/Icon";
import Indicator from "../base/Indicator";
import Job from "../../modules/crm/jobs/models/Job";

/**
 * TagifyJobs — a fully native (no @yaireo/tagify, no dspro.js) job picker.
 *
 * - Initial suggestions come from globalState jobs (useJobs) — no server
 *   request on open.
 * - Typing queries the server (so archived jobs are searchable too).
 * - Supports single and multi select (`multiple`).
 */

interface JobFlag {
    name?: string;
    color?: string;
}

interface JobSuggestion {
    id: string;
    jobId?: string;
    customer?: string;
    status?: string;
    statusColor?: string;
    jobType?: string;
    phone?: string;
    address?: string;
    tech?: string;
    flags?: JobFlag[];
}

interface IProps {
    id: string;
    name?: string;
    label?: string;
    labelUp?: boolean;
    block?: boolean;
    placeholder?: string;
    multiple?: boolean;
    selectedId?: string;
    selectedIds?: string[];
    onSelect?: (jobId?: string, job?: JobSuggestion) => void;
    onRemove?: (jobId?: string) => void;
    onChange?: (ids: string[], jobs: JobSuggestion[]) => void;
}

const fromJob = (j: Job): JobSuggestion => ({
    id: j.id ?? "",
    jobId: j.jobId,
    customer: j.fullName,
    status: j.jobStatus?.name,
    statusColor: j.jobStatus?.color,
    jobType: j.jobType,
    phone: j.phone?.phoneFriendly ?? j.phone?.phone,
    address: j.address?.title,
    tech: j.tech?.fullName,
    flags: (j.flags ?? []).map(f => ({ name: f.name, color: f.color }))
});

const fromServer = (it: any): JobSuggestion => ({
    id: it.value ?? it.id ?? "",
    jobId: it.data?.jobId,
    customer: it.data?.customer,
    status: it.data?.status,
    statusColor: it.data?.statusColor,
    jobType: it.data?.jobType,
    phone: it.data?.phone,
    address: it.data?.address,
    tech: it.data?.tech,
    flags: Array.isArray(it.data?.flags) ? it.data.flags : []
});

export default function TagifyJobs(props: IProps) {

    const hub = useHub();
    const jobs = useJobs();

    const initialList = useMemo<JobSuggestion[]>(() => (jobs ?? []).map(fromJob), [jobs]);

    const controlRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const debounceRef = useRef<any>(null);
    const preloadRef = useRef<string>("");
    const selectingRef = useRef<boolean>(false);

    const [query, setQuery] = useState<string>("");
    const [open, setOpen] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [suggestions, setSuggestions] = useState<JobSuggestion[]>([]);
    const [selectedItems, setSelectedItems] = useState<JobSuggestion[]>([]);
    const [activeIndex, setActiveIndex] = useState<number>(-1);
    const [rect, setRect] = useState<{ top: number; left: number; width: number }>();

    const isSelected = (id: string) => selectedItems.some(x => x.id === id);

    const emit = (list: JobSuggestion[]) => props.onChange?.(list.map(x => x.id), list);

    // Selected items are removed from the suggestion list (they "move up" to the
    // control and come back to the list when removed).
    const visibleSuggestions = suggestions.filter(s => !isSelected(s.id));

    // ----- preselect via selectedId / selectedIds -----
    useEffect(() => {
        const ids = props.multiple ? (props.selectedIds ?? []) : (props.selectedId ? [props.selectedId] : []);
        const key = ids.join(",");
        if (key === preloadRef.current) return;
        preloadRef.current = key;

        if (ids.length === 0) {
            setSelectedItems([]);
            return;
        }

        const resolved: JobSuggestion[] = [];
        const missing: string[] = [];
        ids.forEach(id => {
            const local = initialList.find(s => s.id === id);
            if (local) resolved.push(local);
            else missing.push(id);
        });

        if (missing.length === 0) {
            setSelectedItems(resolved);
            return;
        }
        Promise.all(missing.map(id => new Promise<void>(done => {
            hub.Get(API_ROUTES.TagifyJobs + id, res => {
                if (res?.data) resolved.push(fromServer(res.data));
                done();
            });
        }))).then(() => setSelectedItems(resolved));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.selectedId, props.selectedIds, initialList]);

    // ----- dropdown positioning (portal, fixed) -----
    const updateRect = () => {
        const el = controlRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        setRect({ top: r.bottom + 4, left: r.left, width: r.width });
    };

    useEffect(() => {
        if (!open) return;
        updateRect();
        const handler = () => updateRect();
        window.addEventListener("scroll", handler, true);
        window.addEventListener("resize", handler);
        return () => {
            window.removeEventListener("scroll", handler, true);
            window.removeEventListener("resize", handler);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, selectedItems.length]);

    // ----- outside click -----
    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            // A selection click removes the clicked row from the DOM, which would
            // make the contains() check below fail; skip closing in that case.
            if (selectingRef.current) { selectingRef.current = false; return; }
            const t = e.target as Node;
            if (controlRef.current?.contains(t)) return;
            if (dropdownRef.current?.contains(t)) return;
            setOpen(false);
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [open]);

    // ----- search -----
    const runSearch = (keyword: string) => {
        clearTimeout(debounceRef.current);

        if (!keyword.trim()) {
            setLoading(false);
            setSuggestions(initialList);
            return;
        }

        setLoading(true);
        debounceRef.current = setTimeout(() => {
            hub.Get(API_ROUTES.TagifyJobs + "?keyword=" + encodeURIComponent(keyword.trim()), res => {
                setLoading(false);
                setSuggestions((res?.data ?? []).map(fromServer));
                setActiveIndex(-1);
            });
        }, 300);
    };

    const openDropdown = () => {
        setOpen(true);
        setActiveIndex(-1);
        if (!query.trim()) setSuggestions(initialList);
    };

    const select = (s: JobSuggestion) => {
        if (props.multiple) {
            if (isSelected(s.id)) { remove(s); return; }
            const next = [...selectedItems, s];
            setSelectedItems(next);
            emit(next);
            props.onSelect?.(s.id, s);
            // keep the dropdown open and the current results/search so the user
            // can keep picking; the chosen item just drops out of the list.
            setOpen(true);
            setActiveIndex(-1);
            setTimeout(() => inputRef.current?.focus(), 0);
        } else {
            setSelectedItems([s]);
            emit([s]);
            props.onSelect?.(s.id, s);
            setQuery("");
            setOpen(false);
        }
    };

    const remove = (s: JobSuggestion) => {
        const next = selectedItems.filter(x => x.id !== s.id);
        setSelectedItems(next);
        emit(next);
        props.onRemove?.(s.id);
        if (!props.multiple) props.onSelect?.(undefined, undefined);
    };

    const clearSingle = () => {
        if (selectedItems[0]) remove(selectedItems[0]);
        setQuery("");
        setTimeout(() => { inputRef.current?.focus(); openDropdown(); }, 0);
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) { openDropdown(); return; }
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex(i => Math.min(i + 1, visibleSuggestions.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex(i => Math.max(i - 1, 0));
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (activeIndex >= 0 && visibleSuggestions[activeIndex]) select(visibleSuggestions[activeIndex]);
        } else if (e.key === "Escape") {
            setOpen(false);
        } else if (e.key === "Backspace" && query === "" && props.multiple && selectedItems.length > 0) {
            remove(selectedItems[selectedItems.length - 1]);
        }
    };

    useEffect(() => {
        if (activeIndex < 0 || !dropdownRef.current) return;
        const el = dropdownRef.current.querySelectorAll(".tj-suggestion")[activeIndex] as HTMLElement;
        el?.scrollIntoView({ block: "nearest" });
    }, [activeIndex]);

    // ----- render -----

    const SearchInput = (
        <input ref={inputRef} id={props.id} type="text" autoComplete="off"
            className="tj-input border-0 p-0"
            style={{ outline: "none", background: "transparent" }}
            placeholder={selectedItems.length === 0 ? (props.placeholder ?? "Select a job") : ""}
            value={query}
            onFocus={openDropdown}
            onChange={e => { setQuery(e.target.value); setOpen(true); runSearch(e.target.value); }}
            onKeyDown={onKeyDown} />
    );

    const Pill = (s: JobSuggestion, removable: boolean) => (
        <span key={s.id} className="tj-tag">
            <span className="text-truncate d-flex align-items-center">
                {s.status && <Icon icon="circle" type="solid" color={s.statusColor ?? "#a1a5b7"} size={8} className="me-2" title={s.status} />}
                <span className="fw-bold">#{s.jobId}</span>
                {s.customer && <span className="mx-1 text-gray-400">|</span>}
                {s.customer}
            </span>
            {
                removable &&
                <span className="tj-tag-x" onClick={e => { e.stopPropagation(); remove(s); }}>
                    <Icon icon="xmark" size={11} />
                </span>
            }
        </span>
    );

    const SuggestionRow = (s: JobSuggestion, i: number) => {
        return (
            <div key={s.id || i}
                className={"tj-suggestion" + (i === activeIndex ? " tj-active" : "")}
                onMouseEnter={() => setActiveIndex(i)}
                onMouseDown={e => {
                    e.preventDefault();
                    selectingRef.current = true;
                    select(s);
                    setTimeout(() => { selectingRef.current = false; }, 0);
                }}>

                <div className="d-flex align-items-center w-100">
                    <span className="fw-bold text-gray-900 me-2">#{s.jobId}</span>
                    <span className="text-gray-800 fs-7">{s.customer}</span>
                </div>

                {
                    (s.status || s.jobType || s.tech) &&
                    <div className="d-flex align-items-center flex-wrap mt-2 fs-8 text-gray-600">
                        {
                            s.status &&
                            <span className="d-flex align-items-center me-3">
                                <Icon icon="circle" type="solid" color={s.statusColor ?? "#a1a5b7"} size={8} className="me-1" />
                                {s.status}
                            </span>
                        }
                        {
                            s.jobType &&
                            <span className="d-flex align-items-center me-3">
                                <Icon icon="wrench" className="me-1 text-gray-400" size={11} />
                                {s.jobType}
                            </span>
                        }
                        {
                            s.tech &&
                            <span className="d-flex align-items-center me-3">
                                <Icon icon="user-helmet-safety" className="me-1 text-gray-400" size={11} />
                                {s.tech}
                            </span>
                        }
                    </div>
                }

                {
                    (s.phone || s.address) &&
                    <div className="d-flex align-items-center flex-wrap mt-1 fs-8 text-gray-500">
                        {
                            s.phone &&
                            <span className="d-flex align-items-center me-3">
                                <Icon icon="phone" type="solid" className="me-1 text-gray-400" size={10} />
                                {s.phone}
                            </span>
                        }
                        {
                            s.address &&
                            <span className="d-flex align-items-center tj-address">
                                <Icon icon="location-dot" type="solid" className="me-1 text-gray-400" size={10} />
                                {s.address}
                            </span>
                        }
                    </div>
                }

                {
                    (s.flags?.length ?? 0) > 0 &&
                    <div className="d-flex flex-wrap mt-2">
                        {
                            s.flags!.map((f, k) =>
                                <span key={k} className="badge me-1 mb-1"
                                    style={{ backgroundColor: f.color ?? "#5e6278", color: "#fff" }}>
                                    {f.name}
                                </span>
                            )
                        }
                    </div>
                }
            </div>
        );
    };

    const showInput = props.multiple || selectedItems.length === 0;

    return (
        <div className="row align-items-center mb-10">
            {
                props.label &&
                <label className={"col col-12 " + (props.labelUp ? "mb-1" : (props.block ? "col-md-3" : "col-md-2")) + " fs-7"} htmlFor={props.id}>
                    {props.label}
                    <span id={(props.name ?? props.id) + "-err"} className="small text-danger mx-3 form-error" data-tagify="yes"></span>
                </label>
            }

            <div className={"col col-12 " + (props.labelUp ? "" : (props.block ? "col-md-9" : "col-md-6"))}>

                <div ref={controlRef}
                    className="form-control d-flex align-items-center tj-control"
                    onClick={() => { inputRef.current?.focus(); openDropdown(); }}>

                    {props.multiple && selectedItems.map(s => Pill(s, true))}

                    {
                        !props.multiple && selectedItems.length > 0 &&
                        <div className="tj-pill d-flex align-items-center w-100">
                            <span className="text-truncate d-flex align-items-center">
                                {selectedItems[0].status && <Icon icon="circle" type="solid" color={selectedItems[0].statusColor ?? "#a1a5b7"} size={8} className="me-2" title={selectedItems[0].status} />}
                                <span className="fw-bold">#{selectedItems[0].jobId}</span>
                                {selectedItems[0].customer && <span className="mx-1 text-gray-400">|</span>}
                                {selectedItems[0].customer}
                            </span>
                            <span className="tj-clear ms-auto ps-2" onClick={e => { e.stopPropagation(); clearSingle(); }}>
                                <Icon icon="xmark" />
                            </span>
                        </div>
                    }

                    {showInput && SearchInput}
                </div>

                <input id={props.id + "-val"} type="hidden" name={props.name}
                    value={selectedItems.map(x => x.id).join(",")} readOnly />
            </div>

            {
                open && rect &&
                createPortal(
                    <div ref={dropdownRef} className="tj-dropdown"
                        style={{ top: rect.top, left: rect.left, width: rect.width }}>
                        {
                            loading ?
                                <div className="py-6"><Indicator noText /></div>
                                :
                                visibleSuggestions.length > 0 ?
                                    visibleSuggestions.map((s, i) => SuggestionRow(s, i))
                                    :
                                    <div className="tj-empty">No jobs found</div>
                        }
                    </div>,
                    document.body
                )
            }
        </div>
    );
}
